<template>
  <div class="index" />
</template>

<script>
/**
 * Vue: Index (core)
 * -----------------------------------------------------------------------------
 * Used to set index states and global event listeners.
 * - Also updates component loading states when overlays become active.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import productQuery from '~graphql/product.gql'
import productByVariantQuery from '~graphql/productByVariant.gql'
import productsQuery from '~graphql/products.gql'

import breakpoints from '~/config/breakpoints'
import { cartApiError, registerCartApi } from '~/core/cart-api'
import { openCart } from '~/helpers/cart'
import { convertToShopifyId } from '~/helpers/convert'
import { loginEvent } from '~/helpers/datalayer-actions'
import { formatProduct } from '~/helpers/format'
import { debounce, isCdnUrl, throttle } from '~/helpers/general'
import { getMetafieldIDs, getVariables } from '~/helpers/graphql'
import { getUpsellAddOns } from '~/helpers/product'
import { buildUpsellItems, isUpsellOnly } from '~/helpers/upsell'

import config from '~/../../canvas.config'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Index',

  data() {
    return {
      cnvsGlobal: true,
      screen: {
        breakpoint: 'xs',
        mobile: true,
        height: 0,
        width: 0,
      },
      scroll: {
        direction: 'none',
        header: 'open',
        top: true,
        x: 0,
        y: 0,
      },
      rebuyAdd: false,
      gwpWatcher: null,
      cartApiInFlight: false,
      pageProduct: false,
    }
  },

  computed: {
    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      activeOverlays: (state) => state.overlays.active,
      cart: (state) => state.cart,
      pausedScroll: (state) => state.index.scroll.paused,
      settings: (state) => state.index.settings,
      geolocation: (state) => state.geolocation,
    }),
  },

  watch: {
    /**
     * Watch active overlay to update component loading state.
     */
    getActiveOverlay(overlay) {
      document.body.classList[overlay ? 'add' : 'remove']('has-overlay')

      if (!overlay?.component) {
        return
      }

      const components = Array.isArray(overlay.component) ? overlay.component : [overlay.component]

      components.forEach((handle) => {
        this.loadComponent(handle)
      })
    },
  },

  created() {
    this.gwpWatcher = this.$watch('cart.response', this.handleGwp)
    this.clearAccountData()
    this.handleGeolocation()
    this.setUrlHistory(location.pathname)
  },

  mounted() {
    this.rehydrateSite()
    this.setMobileBreakpoints(config.mobileBreakpoints)
    this.getPreviousUrl()

    this.handleResize()
    this.handleScroll(true)

    this.setEventListeners()
    this.setEventBusListeners()

    this.handleNewsletterResponse()
    this.handleAccountSuccess()

    this.setCartApi()
  },

  methods: {
    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      loadComponent: 'components/load',
      loadStylesheet: 'components/loadStylesheet',
      openOverlay: 'overlays/open',
      rehydrateSite: 'rehydrateSite',
      addItem: 'cart/addItem',
      removeItem: 'cart/updateItem',
      setCartResponse: 'cart/setResponse',
      setMobileBreakpoints: 'setMobileBreakpoints',
      setPreviousUrl: 'setPreviousUrl',
      setScreen: 'setScreen',
      setScroll: 'setScroll',
      setStylesheet: 'setStylesheet',
      setGeolocation: 'geolocation/setGeolocation',
      setUrlHistory: 'setUrlHistory',
    }),

    /**
     * Get previous URL from sessionStorage.
     * - State doesn't update in time if set in the `beforeunload` event.
     */
    getPreviousUrl() {
      const previousUrl = sessionStorage.getItem('previousUrl')

      if (previousUrl) {
        this.setPreviousUrl(previousUrl)
      }
    },

    /**
     * Register the public cart API for third-party integrations.
     * - Anything called before this point was queued and runs now.
     */
    setCartApi() {
      registerCartApi({
        addToCart: this.handleApiAddToCart,
        getUpsells: this.handleApiGetUpsells,
      })
    },

    /**
     * Add a parent item and its upsell-only items to the cart.
     * - The integration sends variant IDs only, the group token tying the
     *   items together is handled here.
     * @param {Object} payload - API payload.
     * @param {Object} payload.parent - Parent item, requires `id`.
     * @param {Array} [payload.upsells] - Upsell items, each requires `id`.
     * @param {Array} [payload.addons] - Independent items to add in the same
     * request, each requires `id`. Not tied to the parent.
     * @param {Boolean} [payload.openDrawer=true] - Open the cart drawer?
     * @returns {Promise<Object>} `cart`, the added `items`, and the `request`
     * that was sent.
     */
    async handleApiAddToCart(payload) {
      const parentId = Number(payload?.parent?.id)

      if (!parentId) {
        throw cartApiError('INVALID_PAYLOAD', 'parent.id must be a variant ID')
      }

      if (this.cartApiInFlight) {
        throw cartApiError('IN_FLIGHT', 'A previous add is still in progress')
      }

      const upsells = Array.isArray(payload.upsells) ? payload.upsells : []

      this.cartApiInFlight = true

      try {
        return await this.addApiItems(payload, parentId, upsells)
      } finally {
        this.cartApiInFlight = false
      }
    },

    /**
     * Validate and add an integration's items to the cart.
     * @param {Object} payload - API payload.
     * @param {Number} parentId - Parent variant ID.
     * @param {Array} upsells - Upsell items, each requires `id`.
     * @returns {Promise<Object>}
     */
    async addApiItems(payload, parentId, upsells) {
      /**
       * Only worth resolving the parent when there are upsells to check it
       * against; Shopify rejects an unavailable variant on its own.
       */
      const parentProduct =
        this.getPageProduct(parentId) ||
        (upsells.length ? await this.queryApiProductByVariant(parentId) : false)

      if (isUpsellOnly(parentProduct)) {
        throw cartApiError(
          'UPSELL_AS_PARENT',
          'This product can only be added as an upsell',
        )
      }

      if (upsells.length && !parentProduct) {
        throw cartApiError('PARENT_UNAVAILABLE', 'This product is unavailable')
      }

      const parentVariant = parentProduct?.variants?.find((variant) => {
        return Number(variant.id) === parentId
      })

      if (parentProduct && !parentVariant?.available) {
        throw cartApiError('PARENT_UNAVAILABLE', 'This product is unavailable')
      }

      await this.validateApiUpsells(parentProduct, upsells)

      const items = buildUpsellItems({
        parent: {
          id: parentId,
          properties: payload.parent.properties,
          quantity: payload.parent.quantity,
        },
        upsells,
      })

      /**
       * Independent items ride along in the same request so the cart only
       * updates once, but they aren't tied to the parent.
       */
      if (Array.isArray(payload.addons)) {
        payload.addons.forEach((addon) => {
          items.push({ ...addon, quantity: addon.quantity || 1 })
        })
      }

      let response = false

      try {
        response = await this.addItem(items)
      } catch (error) {
        throw cartApiError(
          'CART_REJECTED',
          'The cart declined this item',
          error?.description,
        )
      }

      if (payload.openDrawer !== false) {
        openCart(this.getComponentExists, this.openOverlay)
      }

      return {
        cart: this.cart.response,
        items: (response.items || []).map((item) => {
          return {
            key: item.key,
            quantity: item.quantity,
            // eslint-disable-next-line camelcase
            variant_id: item.variant_id,
          }
        }),
        request: items,
      }
    },

    /**
     * Get the upsell-only products approved for a product.
     * @param {String} [handle] - Product handle. Defaults to the product on
     * the current page.
     * @returns {Promise<Array>}
     */
    async handleApiGetUpsells(handle) {
      const product = handle ? await this.queryApiProduct(handle) : this.pageProduct

      if (!product) {
        return []
      }

      const upsells = await this.queryApiUpsells(product)

      return upsells.map((upsell) => {
        return {
          available: upsell.available,
          handle: upsell.handle,
          id: upsell.id,
          price: upsell.price.amount,
          title: upsell.title,
          variants: upsell.variants.map((variant) => {
            return {
              available: variant.available,
              id: variant.id,
              title: variant.title,
            }
          }),
        }
      })
    },

    /**
     * Reject upsells that aren't approved for the parent product.
     * - Stops an integration pairing an upsell with a product merchandising
     *   hasn't approved it for.
     * @param {Object} parentProduct - Canvas formatted parent product.
     * @param {Array} upsells - Upsell items, each requires `id`.
     * @returns {Promise}
     */
    async validateApiUpsells(parentProduct, upsells) {
      if (!upsells.length) {
        return
      }

      const approved = await this.queryApiUpsells(parentProduct)

      const approvedIds = approved.flatMap((upsell) => {
        return upsell.variants.map((variant) => Number(variant.id))
      })

      const unapproved = upsells.find((upsell) => {
        return !approvedIds.includes(Number(upsell.id))
      })

      if (unapproved) {
        throw cartApiError(
          'UPSELL_NOT_ALLOWED',
          'This upsell is not available for this product',
        )
      }
    },

    /**
     * Get the server-rendered product if it matches the given identifier.
     * - Everything the API needs on a product page is already rendered into
     *   it, so this avoids a query and works for products the Storefront API
     *   won't return, such as unlisted ones.
     * @param {Number|String} identifier - Product handle, or one of the
     * product's variant IDs.
     * @returns {Object|Boolean}
     */
    getPageProduct(identifier) {
      const { pageProduct } = this

      if (!pageProduct) {
        return false
      }

      if (pageProduct.handle === identifier) {
        return pageProduct
      }

      const matchesVariant = pageProduct.variants?.some((variant) => {
        return String(variant.id) === String(identifier)
      })

      return matchesVariant ? pageProduct : false
    },

    /**
     * Query a product by handle.
     * @param {String} handle - Product handle.
     * @returns {Promise<Object|Boolean>}
     */
    async queryApiProduct(handle) {
      const pageProduct = this.getPageProduct(handle)

      if (pageProduct) {
        return pageProduct
      }

      const response = await cnvs.Query({
        query: productQuery,
        variables: getVariables({ handle, metafieldIds: this.getApiMetafieldIds() }),
      })

      return response?.product ? formatProduct({ product: response.product }) : false
    },

    /**
     * Query the product a variant belongs to.
     * @param {Number} variantId - Variant ID.
     * @returns {Promise<Object|Boolean>}
     */
    async queryApiProductByVariant(variantId) {
      const pageProduct = this.getPageProduct(variantId)

      if (pageProduct) {
        return pageProduct
      }

      const response = await cnvs.Query({
        query: productByVariantQuery,
        variables: getVariables({
          ids: [convertToShopifyId('ProductVariant', variantId)],
          metafieldIds: this.getApiMetafieldIds(),
        }),
      })

      const product = response?.nodes?.[0]?.product

      return product ? formatProduct({ product }) : false
    },

    /**
     * Query the upsell-only add-on products of a product.
     * @param {Object} product - Canvas formatted product.
     * @returns {Promise<Array>}
     */
    async queryApiUpsells(product) {
      const ids = getUpsellAddOns(product)?.products || []

      if (!ids.length) {
        return []
      }

      /**
       * Server-rendered add-ons are already whole products, only Storefront
       * API responses reference them by ID and need querying.
       */
      if (typeof ids[0] !== 'string') {
        return ids.filter((upsell) => upsell && isUpsellOnly(upsell))
      }

      const response = await cnvs.Query({
        query: productsQuery,
        variables: getVariables({
          ids,
          metafieldIds: this.getApiMetafieldIds(),
        }),
      })

      return response.nodes
        .map((node) => formatProduct({ product: node }))
        .filter((upsell) => upsell && isUpsellOnly(upsell))
    },

    /**
     * Get the metafield identifiers needed to format a product.
     * @returns {Array}
     */
    getApiMetafieldIds() {
      return [...getMetafieldIDs('product'), ...getMetafieldIDs('variant')]
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      this.setStylesheetListeners()

      /**
       * Add default listeners.
       */
      window.visualViewport.addEventListener(
        'resize',
        debounce(() => this.handleResize(), this.settings.throttle),
        { passive: true },
      )

      window.addEventListener(
        'scroll',
        throttle(() => this.handleScroll(), this.settings.throttle),
        { passive: true },
      )

      /**
       * Set previous URL just before changing page.
       */
      window.addEventListener('beforeunload', () => {
        sessionStorage.setItem('previousUrl', location.pathname)
      })

      /**
       * Refresh cart with Rebuy updates
       */
      document.addEventListener('rebuy:cart.add', (payload) => {
        const addedItem = payload.detail?.item?.items[0]

        // eslint-disable-next-line
        if (addedItem?.properties?._momentMaker) {
          return
        }

        this.rebuyAdd = true
      })

      document.addEventListener('rebuy:cart.change', () => {
        cnvs.EventBus.emit('cart:update', this.rebuyAdd)
        this.rebuyAdd = false
      })
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      /**
       * Overlay listeners act as fallbacks for non-Vue components.
       * - Also used by cnvs.Helpers.openOverlay() CLI.
       */
      cnvs.EventBus.listen('overlay:open', (overlay) => {
        this.openOverlay(overlay)

        if (overlay.component || !overlay.stylesheet) {
          return
        }

        this.loadStylesheet(overlay.stylesheet)
      })

      cnvs.EventBus.listen('overlay:close', (overlay) => {
        this.closeOverlay(overlay)
      })

      /**
       * Cart update fallback listener.
       */
      cnvs.EventBus.listen('cart:update', async (open = true) => {
        await this.setCartResponse()
      })

      /**
       * Keep the server-rendered product so the cart API can answer from it
       * rather than querying for data already on the page.
       */
      cnvs.EventBus.listen('main-product-update', (product) => {
        this.pageProduct = product
      })
    },

    /**
     * Listen for stylesheets loading to trigger events after they've loaded.
     */
    setStylesheetListeners() {
      const stylesheets = [...document.querySelectorAll('[rel="stylesheet"]')]

      stylesheets.forEach((element) => {
        const media = element.getAttribute('media')
        let stylesheet = element.getAttribute('href')

        if (isCdnUrl(stylesheet)) {
          stylesheet = stylesheet.split('/').reverse()[0].split('?')[0]
        }

        if (!stylesheet) {
          return
        }

        /**
         * If it's already loaded then add to state.
         */
        if (media === 'all') {
          this.setStylesheet(stylesheet)
          return
        }

        /**
         * Otherwise listen for load.
         */
        element.addEventListener('load', () => {
          element.setAttribute('media', 'all')
          this.setStylesheet(stylesheet)
        })
      })
    },

    /**
     * Handle resize.
     */
    handleResize() {
      this.screen = {
        height: window.visualViewport.height * window.visualViewport.scale,
        width: window.innerWidth,
      }

      /**
       * Set CSS variable.
       */
      document.documentElement.style.setProperty('--viewport-height', `${this.screen.height}px`)

      for (const [breakpoint, value] of Object.entries(breakpoints)) {
        if (!window.matchMedia(`(min-width: ${value})`).matches) {
          break
        }

        this.screen.breakpoint = breakpoint
      }

      this.screen.mobile = this.settings.mobileBreakpoints.includes(this.screen.breakpoint)

      cnvs.EventBus.emit('cnvs:resize', this.screen)
      this.setScreen(this.screen)
    },

    /**
     * Handle scroll.
     * - Don't update state when overlay opens.
     * @param {Boolean} [start] - If setting on initial load.
     */
    handleScroll(start = false) {
      const html = document.documentElement
      if (this.activeOverlays.length) {
        return
      }

      const transparentClass = this.$class('transparent')
      const hasTransparentClass = html.classList.contains(transparentClass)
      const isDefaultTransparent = html.dataset.transparent
      if (window.scrollY > 100 && hasTransparentClass) {
        html.classList.remove(transparentClass)
      } else if (window.scrollY <= 100 && isDefaultTransparent && !hasTransparentClass) {
        html.classList.add(transparentClass)
      }

      /**
       * Set object on start.
       */
      if (start) {
        this.scroll = {
          direction: 'none',
          header: 'open',
          top: window.scrollY <= this.settings.start,
          x: window.scrollX,
          y: window.scrollY,
        }

        return
      }

      /**
       * If scroll is paused or if scroll difference is less than threshold
       * don't update scroll.
       */
      const difference = window.scrollY - this.scroll.y

      if (this.pausedScroll || Math.abs(difference) < this.settings.threshold) {
        return
      }

      this.scroll = {
        direction: difference > 0 ? 'down' : 'up',
        top: window.scrollY <= this.settings.start,
        x: window.scrollX,
        y: window.scrollY,
      }

      if (this.scroll.direction === 'up' && !this.scroll.top) {
        this.scroll.header = 'minimised'
      } else if (window.scrollY > this.settings.start) {
        this.scroll.header = 'collapsed'
      } else {
        this.scroll.header = 'open'
      }

      cnvs.EventBus.emit('cnvs:scroll', this.scroll)
      this.setScroll(this.scroll)
    },

    /**
     * Refocus on footer after newsletter form submission
     */
    handleNewsletterResponse() {
      const shouldFocus = ['customer_posted', 'form_type=customer'].find((key) =>
        window.location.search.includes(key),
      )

      if (!shouldFocus) {
        return
      }

      const field = document.getElementById('newsletter-field')
      if (!field) {
        return
      }

      document.documentElement.style.scrollBehavior = 'auto'
      field.focus()
    },

    /**
     * Handle account login/register success.
     */
    handleAccountSuccess() {
      const accountRegister = sessionStorage.getItem('accountRegister')
      const accountLogin = sessionStorage.getItem('accountLogin')
      const pageType = this.$variable('page.type')
      const customer = this.$variable('customer')
      const deny = ['customers/register', 'customers/activate_account']
      const loginSuccess = Boolean(accountLogin) && pageType === 'customers/account'
      const registerSuccess = Boolean(accountRegister) && !deny.includes(pageType)

      sessionStorage.removeItem('accountRegister')
      sessionStorage.removeItem('accountLogin')

      /**
       * Registration error message
       * Sometimes the error message doesn't appear on form submission
       */
      if (Boolean(accountRegister) && pageType === 'customers/register') {
        const formError = document.querySelector('[js-customer="form-error"]')
        if (formError && !formError.children[0]) {
          formError.innerHTML = `
            <p
              class="
                customers__error
                form-group__note
                text-body-m-regular
              "
            >
              ${this.$string('customers.register.registration_error_html')}
            </p>
          `
        }
      }

      if (!customer?.id || (!loginSuccess && !registerSuccess)) {
        return
      }

      loginEvent({
        id: customer.id,
        email: customer.email,
      })
    },

    /**
     * Remove gift line item automatically if not enabled on page load.
     */
    handleGwp() {
      const { status, response } = this.cart

      if (status.label === 'loading') {
        return
      }
      this.gwpWatcher()

      const { enabled, product } = this.$variable('gwp')
      if (enabled) {
        return
      }

      const giftLine = response.items.find(
        // eslint-disable-next-line no-underscore-dangle
        (item) => item.properties?._isGift && item.product_id === product,
      )

      if (!giftLine) {
        return
      }

      this.removeItem(giftLine.key)
    },

    /**
     * Clear customer data from session storage if they're logged out
     */
    clearAccountData() {
      if (this.$variable('customer.loggedIn')) {
        return
      }
      sessionStorage.removeItem('klaviyo_anniversary')
      sessionStorage.removeItem('klaviyo_birthday')
      sessionStorage.removeItem('klaviyo_experience_ids')
    },

    /**
     * Save user geolocation in Vuex store
     */
    async handleGeolocation() {
      const isGeoSet = sessionStorage.getItem('jh_geo')
      if (isGeoSet) {
        return
      }
      sessionStorage.setItem('jh_geo', 'true')

      try {
        const userGeo = await fetch('https://geo.prod20.narvar.com/postalCode').then((res) =>
          res.json(),
        )
        this.setGeolocation(userGeo)
      } catch (error) {
        console.error('[handleGeolocation]', error)
      }
    },
  },
}
</script>
